import { useAuth } from '../AuthProvider';
import axios, { AxiosError, AxiosInstance } from 'axios';
import { useLayoutEffect } from 'react';
import { PageURL } from '../../router/pageURL';
import { NetworkError } from '../../errors/network-error';
import { ResponseStatusCode } from '../../types/https';
import { config } from '../../utils/config';
import { errorHandler } from '../../api/error-handler';

export const API: AxiosInstance = axios.create({
  baseURL: config.env.VITE_API_URL,
});

/** Pathnames where we do not force redirect to login when signed out (must align with `authRoles: []` routes). */
const PATHNAMES_EXEMPT_FROM_SIGNED_OUT_LOGIN_REDIRECT: readonly string[] = [
  PageURL.LOGIN,
  PageURL.SIGNUP,
  PageURL.CALCULATOR,
  PageURL.NOT_FOUND,
];

function shouldSuppressSignedOutLoginRedirect(): boolean {
  return PATHNAMES_EXEMPT_FROM_SIGNED_OUT_LOGIN_REDIRECT.includes(window.location.pathname);
}

export function useApiInterceptor() {
  const { getToken, isLoaded, isSignedIn, email } = useAuth();

  useLayoutEffect(() => {
    if (isLoaded && isSignedIn === false) {
      if (!shouldSuppressSignedOutLoginRedirect()) {
        window.location.replace(PageURL.LOGIN);
      }
    }
  }, [isLoaded, isSignedIn]);

  useLayoutEffect(() => {
    const requestInterceptorId = API.interceptors.request.use(async requestConfig => {
      if (config.isLocalAuth) {
        requestConfig.headers = {
          ...requestConfig.headers,
          'X-SOURCE-SYSTEM': 'BROKER_PORTAL',
          'X-USE-LOCAL-CLERK-AUTH': 'true',
        };

        return requestConfig;
      }

      if (!isLoaded || !isSignedIn || !email) {
        if (!shouldSuppressSignedOutLoginRedirect()) {
          window.location.replace(PageURL.LOGIN);
        }
        return Promise.reject(new Error('Unauthorized'));
      }

      const token = (await getToken?.()) ?? null;

      if (!token) {
        if (!shouldSuppressSignedOutLoginRedirect()) {
          window.location.replace(PageURL.LOGIN);
        }
        return Promise.reject(new Error('Unauthorized'));
      }

      if (requestConfig.headers) {
        requestConfig.headers = {
          ...requestConfig.headers,
          Authorization: `Bearer ${token}`,
          'X-RETOOL-EMAIL': email,
          'X-SOURCE-SYSTEM': 'BROKER_PORTAL',
        };
      }

      return requestConfig;
    });

    const responseInterceptorId = API.interceptors.response.use(
      response => response,
      (error: AxiosError) => {
        if (error.code === 'ERR_NETWORK') {
          throw new NetworkError("Couldn't connect to the server");
        }

        if (error.response?.status === ResponseStatusCode.UNAUTHORIZED) {
          if (!shouldSuppressSignedOutLoginRedirect()) {
            window.location.replace(PageURL.LOGIN);
          }
        }

        return Promise.reject(errorHandler(error));
      },
    );

    return () => {
      API.interceptors.request.eject(requestInterceptorId);
      API.interceptors.response.eject(responseInterceptorId);
    };
  }, [email]);
}
